import React, { useState } from "react";
import {
  Typography,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Box,
  Divider,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const PaymentMethodCard = ({
  method = {},
  onEdit = () => {},
  onDelete = () => {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const type = (method.type || "Unknown").toLowerCase();
  const isCard = type === "card";

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: "0 3px 10px rgba(0,0,0,0.05)",
        transition: "all 0.25s ease",
        inlineSize: 300,
        blockSize: 180,
        overflow: "hidden",
        background: "linear-gradient(to bottom right, #fff, #f9fafb)",
        "&:hover": {
          boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
          transform: "translateY(-3px)",
        },
      }}
    >
      <CardContent
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
        }}
      >
        {/* Header Section */}
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          {/* Left: Image + Info */}
          <Box
            display="flex"
            alignItems="flex-start"
            gap={1.8}
            overflow="hidden"
          >
            {/* Dynamic Image */}
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                overflow: "hidden",
                flexShrink: 0,
                bgcolor: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {method.icon ? (
                <img
                  src={method.icon}
                  alt={method.type}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#94a3b8",
                    fontSize: 12,
                    fontWeight: 500,
                    textAlign: "center",
                    p: 1,
                  }}
                >
                  No Image
                </Box>
              )}
            </Box>

            {/* Text Info */}
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 0.4,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  textTransform: "capitalize",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {method.type || "Unknown"}
              </Typography>

              {method.accountName && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={method.accountName}
                >
                  {method.accountName}
                </Typography>
              )}

              <Chip
                label={method.isActive ? "Active" : "Inactive"}
                size="small"
                sx={{
                  alignSelf: "flex-start",
                  mt: 0.4,
                  fontWeight: 600,
                  textTransform: "capitalize",
                  bgcolor: method.isActive ? "#ecfdf5" : "#f1f5f9",
                  color: method.isActive ? "#059669" : "#475569",
                  borderRadius: 1.5,
                  height: 22,
                }}
              />
            </Box>
          </Box>

          {/* Right: Dropdown Menu */}
          <Tooltip title="More options">
            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreVert />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                onEdit(method);
              }}
            >
              <EditIcon fontSize="small" sx={{ mr: 1, color: "#1976d2" }} />{" "}
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                onDelete(method._id);
              }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1, color: "#dc2626" }} />{" "}
              Delete
            </MenuItem>
          </Menu>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Details Section */}
        <Box display="flex" flexDirection="column" gap={0.6} overflow="hidden">
          {method.accountValue && (
            <Tooltip title={method.accountValue}>
              <Typography
                variant="body2"
                sx={{
                  display: "flex",
                  gap: 0.5,
                  color: "#475569",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <strong>Value:</strong> {method.accountValue}
              </Typography>
            </Tooltip>
          )}

          {isCard && (
            <>
              {method.depositLink && (
                <Tooltip title={method.depositLink}>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      color: "#475569",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <strong>Deposit:</strong>{" "}
                    <a
                      href={method.depositLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#2563eb", textDecoration: "none" }}
                    >
                      {method.depositLink}
                    </a>
                  </Typography>
                </Tooltip>
              )}

              {method.withdrawLink && (
                <Tooltip title={method.withdrawLink}>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      color: "#475569",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <strong>Withdraw:</strong>{" "}
                    <a
                      href={method.withdrawLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#2563eb", textDecoration: "none" }}
                    >
                      {method.withdrawLink}
                    </a>
                  </Typography>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaymentMethodCard;
